import apiService from './api';

export interface User {
  id: string;
  company_id?: string; // Backend returns company_id (snake_case), optional for admin users
  name: string;
  email: string;
  role: string;
  permissions: string[];
  password?: string; // Only present in create response
}

export interface CreateUserPayload {
  name: string;
  email: string;
  role: string;
  permissions: string[];
  companyId?: string; // Optional - admin can specify, manager/user will use token
}

export interface UpdateUserPayload {
  name?: string;
  email?: string;
  role?: string;
  permissions?: string[];
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

class UserService {
  async createUser(payload: CreateUserPayload): Promise<User> {
    const response = await apiService.post<User>('/admin/users', payload);
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data!;
  }

  async listUsers(params?: { search?: string; page?: number; limit?: number }): Promise<PaginatedResponse<User>> {
    const queryParams = new URLSearchParams();
    
    // Don't send companyId - BFF will get it from token
    // Admin users will see all users, managers/users will see only their company users
    
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const endpoint = `/admin/users?${queryParams.toString()}`;
    const response = await apiService.get<PaginatedResponse<User>>(endpoint);
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data!;
  }

  async updateUser(id: string, payload: UpdateUserPayload): Promise<User> {
    const response = await apiService.put<User>(`/admin/users/${id}`, payload);
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data!;
  }

  async deleteUser(id: string): Promise<void> {
    const response = await apiService.delete<void>(`/admin/users/${id}`);
    if (response.error) {
      throw new Error(response.error);
    }
  }

  async changePassword(userId: string, newPassword: string, currentPassword?: string): Promise<void> {
    const payload: any = { password: newPassword };
    if (currentPassword) {
      payload.currentPassword = currentPassword;
    }
    const response = await apiService.put<{ message: string }>(`/users/${userId}/change-password`, payload);
    if (response.error) {
      // Translate known backend error messages to Portuguese
      const errorMap: Record<string, string> = {
        'invalid current password': 'Senha atual incorreta',
        'password must be at least 6 characters': 'A senha deve ter no mínimo 6 caracteres',
        'current password is required when changing your own password': 'A senha atual é obrigatória para alterar sua própria senha',
        'password is required': 'A nova senha é obrigatória',
      };
      const friendlyMessage = errorMap[response.error.toLowerCase()] || response.error;
      throw new Error(friendlyMessage);
    }
  }
}

const userService = new UserService();
export default userService;
